import moment from "moment";

import type {
  ITransactionRecurrence,
  RecurrenceDayOfWeek,
  RecurrenceFrequency,
  TransactionBackendStatus,
} from "@types";

const FREQUENCY_MAP: Record<string, RecurrenceFrequency> = {
  dia: "DAILY",
  semana: "WEEKLY",
  mes: "MONTHLY",
  ano: "YEARLY",
};

const DAY_OF_WEEK_MAP: Record<string, RecurrenceDayOfWeek> = {
  mon: "MONDAY",
  tue: "TUESDAY",
  wed: "WEDNESDAY",
  thu: "THURSDAY",
  fri: "FRIDAY",
  sat: "SATURDAY",
  sun: "SUNDAY",
};

const STATUS_MAP: Record<string, TransactionBackendStatus> = {
  previsto: "FORECASTED",
  realizado: "REALIZED",
  vencido: "OVERDUE",
  ematraso: "OVERDUE",
};

export const toBackendStatus = (formStatus?: string | null): TransactionBackendStatus | undefined => {
  const normalized = formStatus?.trim().toLowerCase();
  return normalized ? STATUS_MAP[normalized] : undefined;
};

const FREQUENCY_TO_FORM = Object.fromEntries(
  Object.entries(FREQUENCY_MAP).map(([formId, apiValue]) => [apiValue, formId])
) as Record<RecurrenceFrequency, string>;

const DAY_OF_WEEK_TO_FORM = Object.fromEntries(
  Object.entries(DAY_OF_WEEK_MAP).map(([formId, apiValue]) => [apiValue, formId])
) as Record<RecurrenceDayOfWeek, string>;

type RecurrenceFormFields = {
  isRecurring?: boolean;
  repeatEvery?: string;
  weekDays?: string[];
  recurrenceStart?: string;
  recurrenceEnd?: string;
  recurrenceInterval?: number;
  dueDate?: string;
  date?: string;
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const lastDayOfMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Para recorrência anual, mantém o dia/mês do vencimento e troca apenas o ano
 * selecionado (ex.: vencimento 15/03 + ano 2027 => 2027-03-15).
 * Sem vencimento de referência, cai no primeiro dia do ano.
 */
export const toYearAnchoredDate = (value?: string | null, anchor?: string | null): string | undefined => {
  const year = value?.trim().slice(0, 4);
  if (!year || !/^\d{4}$/.test(year)) return undefined;

  const anchorParts = anchor?.trim().match(ISO_DATE_PATTERN);
  if (!anchorParts) return `${year}-01-01`;

  const month = Number(anchorParts[2]);
  if (month < 1 || month > 12) return `${year}-01-01`;

  const day = Math.min(Number(anchorParts[3]), lastDayOfMonth(Number(year), month));
  if (day < 1) return `${year}-01-01`;

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const getYearFromDate = (value?: string | null): string => {
  const year = value?.trim().slice(0, 4);
  return year && /^\d{4}$/.test(year) ? year : "";
};

export const buildRecurrencePayload = (form: RecurrenceFormFields): ITransactionRecurrence | undefined => {
  if (!form.isRecurring) return undefined;

  const frequency = form.repeatEvery ? FREQUENCY_MAP[form.repeatEvery.trim().toLowerCase()] : undefined;
  if (!frequency) return undefined;

  const daysOfWeek =
    frequency === "WEEKLY"
      ? (form.weekDays ?? [])
          .map((day) => DAY_OF_WEEK_MAP[day])
          .filter((day): day is RecurrenceDayOfWeek => Boolean(day))
      : [];

  const startDate = form.recurrenceStart || form.dueDate || form.date;
  const endDate = form.recurrenceEnd || undefined;

  if (frequency === "YEARLY") {
    const anchor = form.dueDate || form.date;
    return {
      frequency,
      interval: form.recurrenceInterval && form.recurrenceInterval > 0 ? form.recurrenceInterval : 1,
      daysOfWeek: null,
      startDate: toYearAnchoredDate(startDate, anchor),
      endDate: toYearAnchoredDate(endDate, anchor),
    };
  }

  return {
    frequency,
    interval: form.recurrenceInterval && form.recurrenceInterval > 0 ? form.recurrenceInterval : 1,
    daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : null,
    startDate,
    endDate,
  };
};

export const RECURRENCE_YEAR_RANGE = 5;

export const MAX_RECURRENCE_YEARS_AHEAD = 4;

const RECURRENCE_DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY"];

export const parseRecurrenceDate = (value?: string | null): moment.Moment | undefined => {
  const parsed = moment(value ?? "", RECURRENCE_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : undefined;
};

export const getRecurrenceMaxEndDate = (startDate?: string | null): moment.Moment | undefined =>
  parseRecurrenceDate(startDate)?.clone().add(MAX_RECURRENCE_YEARS_AHEAD, "years");

export const isRecurrenceEndDateAllowed = (candidate: moment.Moment, startDate?: string | null): boolean => {
  const start = parseRecurrenceDate(startDate);
  if (!start) return true;

  const maxEndDate = start.clone().add(MAX_RECURRENCE_YEARS_AHEAD, "years");
  return !candidate.isBefore(start, "day") && !candidate.isAfter(maxEndDate, "day");
};

export const getRecurrencePeriodErrorKey = (startDate?: string | null, endDate?: string | null): string | undefined => {
  const end = parseRecurrenceDate(endDate);
  if (!end) return undefined;

  const start = parseRecurrenceDate(startDate);
  if (!start) return undefined;

  if (end.isBefore(start, "day")) return "recurrence.error.invalidPeriod";

  return end.isAfter(start.clone().add(MAX_RECURRENCE_YEARS_AHEAD, "years"), "day")
    ? "recurrence.error.limitExceeded"
    : undefined;
};

export const buildYearOptions = (
  reference?: string | null,
  anchor?: string | null
): { id: string; label: string }[] => {
  const currentYear = new Date().getFullYear();
  const years = new Set<number>();

  for (let offset = 0; offset < RECURRENCE_YEAR_RANGE; offset += 1) {
    years.add(currentYear + offset);
  }

  const referenceYear = Number(getYearFromDate(reference));
  if (referenceYear) years.add(referenceYear);

  return Array.from(years)
    .sort((a, b) => a - b)
    .map((year) => ({
      id: toYearAnchoredDate(String(year), anchor) ?? `${year}-01-01`,
      label: String(year),
    }));
};

export const buildRecurrenceEndYearOptions = (
  startDate?: string | null,
  anchor?: string | null
): { id: string; label: string }[] => {
  const start = parseRecurrenceDate(startDate);
  const startYear = start ? start.year() : new Date().getFullYear();

  return Array.from({ length: MAX_RECURRENCE_YEARS_AHEAD + 1 }, (_, offset) => startYear + offset).map((year) => ({
    id: toYearAnchoredDate(String(year), anchor) ?? `${year}-01-01`,
    label: String(year),
  }));
};

export const toFormDate = (value?: string | null): string => (value ? value.split("T")[0] : "");

export type RecurrenceFormValues = {
  isRecurring: boolean;
  repeatEvery: string;
  weekDays: string[];
  recurrenceStart: string;
  recurrenceEnd: string;
  recurrenceInterval: number;
};

const EMPTY_RECURRENCE_FORM: RecurrenceFormValues = {
  isRecurring: false,
  repeatEvery: "",
  weekDays: [],
  recurrenceStart: "",
  recurrenceEnd: "",
  recurrenceInterval: 1,
};

export const parseRecurrenceFromApi = (
  recurrence?: Partial<ITransactionRecurrence> | null,
  recurrenceId?: string | null
): RecurrenceFormValues => {
  if (!recurrence) {
    return recurrenceId ? { ...EMPTY_RECURRENCE_FORM, isRecurring: true } : EMPTY_RECURRENCE_FORM;
  }

  const repeatEvery = recurrence.frequency ? (FREQUENCY_TO_FORM[recurrence.frequency] ?? "") : "";

  return {
    isRecurring: true,
    repeatEvery,
    weekDays: (recurrence.daysOfWeek ?? [])
      .map((day) => DAY_OF_WEEK_TO_FORM[day])
      .filter((day): day is string => !!day),
    recurrenceStart: toFormDate(recurrence.startDate),
    recurrenceEnd: toFormDate(recurrence.endDate),
    recurrenceInterval: recurrence.interval && recurrence.interval > 0 ? recurrence.interval : 1,
  };
};
